import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconClose, IconPlus } from '../../UI';
import { Button } from '../../UI/base/Button';
import { FloatingPopover } from './FloatingPopover';
import { EDITOR_DAYS } from './constants';
import type { EditorAllowedDay, EditorDayKey, EditorNode } from './types';
import type { TimeWindow } from '../../../api/generated';
import styles from '../InterventionGraphEditor.module.css';

// Local-only draft shapes: time windows carry a stable `_key` so React can track
// rows across add/remove (the API `TimeWindow` has no id). Stripped on save.
interface DraftTimeWindow extends TimeWindow {
  _key: string;
}

interface DraftDay {
  day: EditorDayKey;
  timeWindows: DraftTimeWindow[];
}

const makeWindow = (): DraftTimeWindow => ({
  _key: crypto.randomUUID(),
  start: '08:00',
  end: '16:00'
});

const setWindowTime = (
  days: DraftDay[],
  day: EditorDayKey,
  idx: number,
  field: 'start' | 'end',
  value: string
): DraftDay[] =>
  days.map((d) =>
    d.day !== day
      ? d
      : {
          ...d,
          timeWindows: d.timeWindows.map((tw, i) => (i === idx ? { ...tw, [field]: value } : tw))
        }
  );

const addDayWindow = (days: DraftDay[], day: EditorDayKey): DraftDay[] =>
  days.map((d) => (d.day !== day ? d : { ...d, timeWindows: [...d.timeWindows, makeWindow()] }));

const removeDayWindow = (days: DraftDay[], day: EditorDayKey, idx: number): DraftDay[] =>
  days.map((d) =>
    d.day !== day ? d : { ...d, timeWindows: d.timeWindows.filter((_, i) => i !== idx) }
  );

interface AllowedDaysModalProps {
  wi: EditorNode;
  interventionName: string;
  strokeColor: string;
  triggerRect: DOMRect | null;
  onSave: (allowedDays: EditorAllowedDay[] | undefined) => void;
  onClose: () => void;
}

export const AllowedDaysModal = ({
  wi,
  interventionName,
  strokeColor,
  triggerRect,
  onSave,
  onClose
}: AllowedDaysModalProps) => {
  const { t } = useTranslation();
  const [restricted, setRestricted] = useState(wi.allowedDays !== undefined);
  const [days, setDays] = useState<DraftDay[]>(() =>
    (wi.allowedDays ?? []).map((d) => ({
      day: d.day,
      timeWindows: d.timeWindows.map((tw) => ({ ...tw, _key: crypto.randomUUID() }))
    }))
  );

  const toggleDay = (day: EditorDayKey) => {
    setDays((prev) => {
      const has = prev.some((d) => d.day === day);
      if (has) return prev.filter((d) => d.day !== day);
      return [...prev, { day, timeWindows: [makeWindow()] }];
    });
  };

  const updateTime = (
    day: EditorDayKey,
    idx: number,
    field: 'start' | 'end',
    value: string
  ) => {
    setDays((prev) => setWindowTime(prev, day, idx, field, value));
  };

  const addWindow = (day: EditorDayKey) => {
    setDays((prev) => addDayWindow(prev, day));
  };

  const removeWindow = (day: EditorDayKey, idx: number) => {
    setDays((prev) => removeDayWindow(prev, day, idx));
  };

  const handleSave = () => {
    const editorDays: EditorAllowedDay[] = days.map((d) => ({
      day: d.day,
      timeWindows: d.timeWindows.map((tw) => ({ start: tw.start, end: tw.end }))
    }));
    onSave(restricted ? editorDays : undefined);
    onClose();
  };

  return (
    <FloatingPopover open triggerRect={triggerRect} onClose={onClose} width={400}>
      <div className={styles.popoverHeader}>
        {t('config-days-title')} — {interventionName}
      </div>
      <div className={styles.popoverBody}>
        <div className={styles.daysModal}>
          <label className={styles.daysToggle}>
            <input
              type="checkbox"
              checked={restricted}
              onChange={(e) => setRestricted(e.target.checked)}
            />
            <span>{t('config-days-restrict')}</span>
          </label>

          {!restricted && <p className={styles.daysHint}>{t('config-days-all-hint')}</p>}

          {restricted && (
            <div className={styles.daysGrid}>
              {EDITOR_DAYS.map((day) => {
                const dayData = days.find((d) => d.day === day);
                return (
                  <div key={day} className={styles.dayEntry}>
                    <label className={styles.dayCheckbox}>
                      <input type="checkbox" checked={!!dayData} onChange={() => toggleDay(day)} />
                      <span
                        className={styles.dayName}
                        style={dayData ? { color: strokeColor, fontWeight: 700 } : {}}
                      >
                        {t(`config-day-${day.toLowerCase()}`)}
                      </span>
                    </label>
                    {dayData && (
                      <div className={styles.dayWindows}>
                        {dayData.timeWindows.map((tw, i) => (
                          <div key={tw._key} className={styles.timeRow}>
                            <input
                              type="time"
                              className={styles.timeInput}
                              value={tw.start ?? ''}
                              onChange={(e) => updateTime(day, i, 'start', e.target.value)}
                            />
                            <span className={styles.timeSep}>–</span>
                            <input
                              type="time"
                              className={styles.timeInput}
                              value={tw.end ?? ''}
                              onChange={(e) => updateTime(day, i, 'end', e.target.value)}
                            />
                            {dayData.timeWindows.length > 1 && (
                              <button
                                className={styles.windowRemoveBtn}
                                type="button"
                                onClick={() => removeWindow(day, i)}
                                aria-label={t('config-cancel')}
                              >
                                <IconClose width={14} height={14} />
                              </button>
                            )}
                          </div>
                        ))}
                        <button
                          type="button"
                          className={styles.addWindowBtn}
                          onClick={() => addWindow(day)}
                        >
                          <IconPlus width={14} height={14} /> {t('config-days-add-window')}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div className={styles.popoverFooter}>
        <Button type="secondary" onClick={onClose}>
          {t('config-cancel')}
        </Button>
        <Button type="primary" onClick={handleSave}>
          {t('config-days-save')}
        </Button>
      </div>
    </FloatingPopover>
  );
};
